import {
  BrowserHistoryBuildOptions,
  createBrowserHistory,
  History,
  LocationDescriptorObject,
  Path,
} from 'history';

export type ForwardActions = 'push' | 'replace';

interface AppHistoryState {
  appDepth?: number;
}

let history: History<unknown>;

const withAppDepth = (state: unknown, appDepth: number) => ({
  ...(typeof state === 'object' && state !== null ? state : {}),
  appDepth,
});

// Tags every history entry created from inside the app with how many
// in-app navigations deep it is, so we can later tell whether "going back"
// would stay inside the app or exit it (e.g. entries from a pasted URL or
// an external referrer are never tagged, so their depth is 0).
const wrapForAppDepth = (
  historyToWrap: History<unknown>,
  method: 'push' | 'replace',
  getNextDepth: (currentDepth: number) => number
) => {
  const original = historyToWrap[method].bind(historyToWrap);

  historyToWrap[method] = ((
    location: Path | LocationDescriptorObject<unknown>,
    state?: unknown
  ) => {
    const currentDepth =
      (historyToWrap.location.state as AppHistoryState | undefined)?.appDepth ??
      0;
    const nextDepth = getNextDepth(currentDepth);

    if (typeof location === 'object') {
      original({
        ...location,
        state: withAppDepth(location.state ?? state, nextDepth),
      });
      return;
    }

    original(location, withAppDepth(state, nextDepth));
  }) as History<unknown>[typeof method];
};

export const createHistory = (args?: BrowserHistoryBuildOptions) => {
  history = createBrowserHistory(args);

  wrapForAppDepth(history, 'push', (depth) => depth + 1);
  // A replace doesn't add a step to the stack, so it keeps the same depth
  // the current entry already had.
  wrapForAppDepth(history, 'replace', (depth) => depth);

  return history;
};

// True when the entry we'd land on via `history.goBack()` is guaranteed to
// be one created from inside the app (as opposed to a pasted URL or an
// external referrer, which are never depth-tagged).
export const canGoBackInApp = (historyToCheck: History<unknown>): boolean =>
  ((historyToCheck.location.state as AppHistoryState | undefined)?.appDepth ??
    0) > 0;
